import { withDevCycleProvider } from '@devcycle/react-client-sdk';
import * as Sentry from '@sentry/react';
import { Suspense } from 'react';
import useAppTheme from '@/components/App/hooks/useAppTheme';
import useHandleUserTracking from '@/components/App/hooks/useHandleUserTracking';
import useLogoutIfAuthTokenExpired from '@/components/App/hooks/useLogoutIfAuthTokenExpired';
import useScrollTop from '@/components/App/hooks/useScrollTop';
import useTrackPageView from '@/components/App/hooks/useTrackPageView';
import { reloadIfStaleChunkError } from '@/components/App/reloadIfStaleChunkError/reloadIfStaleChunkError';
import ErrorFallback from '@/components/error/ErrorFallback';
import Html from '@/components/Html/Html';
import ModalRenderer from '@/components/ModalRenderer';
import PaymentProviderProvider from '@/components/PaymentProvider/PaymentProviderProvider';
import { ToastContainer } from '@/components/Toast';
import { getConfig } from '@/constants/config';
import { LocalStorageKeys } from '@/constants/LocalStorage';
import { DevTools, shouldRenderDevTools } from '@/devtools/load';
import useApplyFeatureFlagQueryParam from '@/featureFlags/useApplyFeatureFlagQueryParam';
import useSyncDevCycleFlagsToPostHog from '@/integrations/useSyncDevCycleFlagsToPostHog';
import Pages from '@/routes/Pages';
import { environment, isMockingEnabled } from '@/utils/env.utils';
import useStoreTrackingData from '@/utils/tracking/useStoreTrackingData';

function App() {
  useAppTheme();
  useHandleUserTracking();
  useLogoutIfAuthTokenExpired();
  useStoreTrackingData();
  useScrollTop();
  useTrackPageView();
  useApplyFeatureFlagQueryParam();
  useSyncDevCycleFlagsToPostHog();

  return (
    <>
      <Sentry.ErrorBoundary fallback={<ErrorFallback />} onError={reloadIfStaleChunkError}>
        <PaymentProviderProvider>
          <Html />
          <ToastContainer limit={1} />
          <ModalRenderer />
          <Pages />
          {shouldRenderDevTools() && (
            <Suspense fallback={null}>
              <DevTools />
            </Suspense>
          )}
        </PaymentProviderProvider>
      </Sentry.ErrorBoundary>
    </>
  );
}

const userId = localStorage.getItem(LocalStorageKeys.USER_ID);
const AppWithDevCycleProvider = withDevCycleProvider({
  sdkKey: environment.dvcSdkKey,
  user: {
    // ISO 3166-1 alpha-2 for flag targeting (our UK market uses GB).
    country: getConfig().isoCountryCode,
    ...(userId ? { user_id: userId } : {}),
  },
  options: {
    // disable DevCycle real-time updates when mocking because MSW doesn't work well with them
    disableRealtimeUpdates: isMockingEnabled(),
  },
})(App);

export default AppWithDevCycleProvider;
